import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

interface PermanentDeleteConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string;
  doctype: string;
  onConfirm: () => void;
  isDeleting: boolean;
}

/**
 * Final confirmation before permanent deletion.
 *
 * For Item doctype: requires typing the item name to confirm.
 * For other doctypes: standard destructive confirmation (no input).
 */
export function PermanentDeleteConfirm({
  open,
  onOpenChange,
  itemName,
  doctype,
  onConfirm,
  isDeleting,
}: PermanentDeleteConfirmProps) {
  const [confirmText, setConfirmText] = useState("");
  const requiresTypeConfirm = doctype === "Item";
  const isConfirmed = requiresTypeConfirm
    ? confirmText.trim() === itemName
    : true;

  // Reset input when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmText("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Permanently delete &ldquo;{itemName}&rdquo;?
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            This will remove the {doctype === "Item" ? "product" : doctype.toLowerCase()} and all its
            data. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {requiresTypeConfirm && (
          <div className="space-y-2 py-2">
            <label
              htmlFor="confirm-delete-input"
              className="text-sm text-muted-foreground"
            >
              Type &ldquo;{itemName}&rdquo; to confirm:
            </label>
            <Input
              id="confirm-delete-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={itemName}
              aria-describedby="confirm-delete-hint"
              autoComplete="off"
              autoFocus
            />
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!isConfirmed || isDeleting}
          >
            {isDeleting && <Loader2 className="size-4 mr-2 animate-spin" />}
            {isDeleting ? "Deleting..." : "Delete forever"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
